const chapter = {
  id: 's-block',
  slug: 's-block',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 's-Block Elements — Alkali & Alkaline-Earth Metals',
        hindi: 's-खण्ड तत्व — क्षार और क्षारीय-मृदा धातुएँ',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'group1-general',
      title: {
        english: 'Group 1 — Alkali Metals (Li, Na, K, Rb, Cs, Fr)',
        hindi: 'समूह 1 - क्षार धातुएँ (Li, Na, K, Rb, Cs, Fr)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Outer config $ns^1$. Always +1 oxidation state. Strongly electropositive; lowest IE in their period.',
        hindi: 'बाहरी कॉन्फ़िगरेशन $ns^1$। सदैव +1 ऑक्सीकरण अवस्था। अत्यधिक विद्युत धनात्मक; उनकी अवधि में सबसे कम IE।',
            },
            {
              english: 'Soft, silvery-white; can be cut with a knife. Low densities (Li, Na, K float on water).',
        hindi: 'नरम, चांदी जैसा सफेद; चाकू से काटा जा सकता है. कम घनत्व (Li, Na, K पानी पर तैरते हैं)।',
            },
            {
              english: 'Down the group: atomic / ionic radius **increases**, IE **decreases**, m.p./b.p. **decrease**, density generally **increases** (K is anomalous, lighter than Na).',
        hindi: 'समूह में नीचे: परमाणु/आयनिक त्रिज्या **बढ़ती**, IE **घटती**, m.p./b.p. **कमी**, घनत्व आम तौर पर **बढ़ता है** (K असामान्य है, Na से हल्का है)।',
            },
            {
              english: 'Stored under kerosene because they react vigorously with air & moisture.',
        hindi: 'मिट्टी के तेल के नीचे संग्रहित किया जाता है क्योंकि वे हवा और नमी के साथ तीव्र प्रतिक्रिया करते हैं।',
            },
            {
              english: 'Strong reducing agents in solution. Order of reducing power in aqueous solution: **Li > Cs > Na > K > Rb** — Li is strongest because of very high hydration enthalpy of $Li^+$ (high charge density).',
        hindi: 'समाधान में मजबूत कम करने वाले एजेंट। जलीय घोल में शक्ति कम करने का क्रम: **Li > Cs > Na > K > Rb** - $Li^+$ (उच्च चार्ज घनत्व) की बहुत उच्च जलयोजन एन्थैल्पी के कारण Li सबसे मजबूत है।',
            },
            {
              english: 'Impart characteristic colours to flame: Li (crimson), Na (golden), K (lilac), Rb (red-violet), Cs (blue) — due to easy excitation of valence electron.',
        hindi: 'ज्वाला को विशिष्ट रंग प्रदान करें: ली (क्रिमसन), Na (सुनहरा), K (बकाइन), Rb (लाल-बैंगनी), Cs (नीला) - वैलेंस इलेक्ट्रॉन के आसान उत्तेजना के कारण।',
            },
            {
              english: 'Solutions in liquid NH₃ are deep blue (dilute) → bronze (concentrated); contain ammoniated electrons; paramagnetic & strongly reducing.',
        hindi: 'तरल NH₃ में घोल गहरे नीले (पतला) → कांस्य (केंद्रित) होते हैं; अम्मोनीकृत इलेक्ट्रॉन होते हैं; अनुचुंबकीय और दृढ़ता से कम करने वाला।',
            },
          ],
        },
      ],
    },
    {
      id: 'group1-reactions',
      title: {
        english: 'Group 1 — Reactions',
        hindi: 'वर्ग 1 — अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'With **air / O₂**: $4Li + O_2 \\to 2Li_2O$ (mono-oxide); $2Na + O_2 \\to Na_2O_2$ (peroxide); $K, Rb, Cs + O_2 \\to MO_2$ (super-oxide). Heavier metals stabilise larger anions ($O_2^{2-}, O_2^-$).',
        hindi: '**वायु / O₂** के साथ: $4Li + O_2 \\to 2Li_2O$ (मोनो-ऑक्साइड); $2Na + O_2 \\to Na_2O_2$ (पेरोक्साइड); $K, Rb, Cs + O_2 \\to MO_2$ (सुपर-ऑक्साइड)। भारी धातुएँ बड़े आयनों ($O_2^{2-}, O_2^-$) को स्थिर करती हैं।',
            },
            {
              english: 'With **water**: $2M + 2H_2O \\to 2MOH + H_2$. Reactivity increases down the group; Li reacts gently, K catches fire, Cs explosively.',
        hindi: '**पानी** के साथ: $2M + 2H_2O \\to 2MOH + H_2$। समूह में नीचे की ओर प्रतिक्रियाशीलता बढ़ती है; ली धीरे से प्रतिक्रिया करता है, K आग पकड़ लेता है, Cs विस्फोटक ढंग से प्रतिक्रिया करता है।',
            },
            {
              english: 'With **dihydrogen**: $2M + H_2 \\to 2MH$ (saline ionic hydride).',
        hindi: '**डायहाइड्रोजन** के साथ: $2M + H_2 \\to 2MH$ (खारा आयनिक हाइड्राइड)।',
            },
            {
              english: 'With **halogens**: $2M + X_2 \\to 2MX$ (ionic salts; LiI shows max covalent character, Fajans).',
        hindi: '**हैलोजन** के साथ: $2M + X_2 \\to 2MX$ (आयनिक लवण; LiI अधिकतम सहसंयोजक चरित्र दिखाता है, फ़ैजंस)।',
            },
            {
              english: 'With **NH₃ (liquid)**: dissolve to form blue solutions; with hot conc. NH₃ over Fe catalyst: $2M + 2NH_3 \\to 2MNH_2 + H_2$ (amides).',
        hindi: '**NH₃ (तरल)** के साथ: नीला घोल बनाने के लिए घुलना; गर्म सांद्रण के साथ. Fe उत्प्रेरक पर NH₃: $2M + 2NH_3 \\to 2MNH_2 + H_2$ (एमाइड्स)।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Anomalous behaviour of **Li** (small size, high polarising power): forms only mono-oxide, hydrogen carbonate is unstable as solid, LiCl is deliquescent (covalent), Li₂CO₃ decomposes on heating to Li₂O + CO₂; resembles Mg (diagonal relationship).',
        hindi: '**Li** (छोटा आकार, उच्च ध्रुवीकरण शक्ति) का असामान्य व्यवहार: केवल मोनो-ऑक्साइड बनाता है, हाइड्रोजन कार्बोनेट ठोस के रूप में अस्थिर होता है, LiCl द्रवीकृत (सहसंयोजक) होता है, Li₂CO₃ गर्म करने पर Li₂O + CO₂ में विघटित हो जाता है; एमजी (विकर्ण संबंध) जैसा दिखता है।',
          },
        },
      ],
    },
    {
      id: 'group1-compounds',
      title: {
        english: 'Important Compounds of Sodium',
        hindi: 'सोडियम के महत्वपूर्ण यौगिक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**NaOH (caustic soda)** — by **Castner-Kellner** process (Hg cathode) or membrane-cell electrolysis of brine. Strong base, deliquescent, used in soap, paper, rayon, refining of petroleum.',
        hindi: '**NaOH (कास्टिक सोडा)** - **कास्टनर-केल्नर** प्रक्रिया (Hg कैथोड) या नमकीन पानी की झिल्ली-कोशिका इलेक्ट्रोलिसिस द्वारा। मजबूत आधार, द्रवीकरण, साबुन, कागज, रेयान, पेट्रोलियम के शोधन में उपयोग किया जाता है।',
            },
            {
              english: '**Na₂CO₃ (washing soda)** — **Solvay (ammonia-soda) process**: $NH_3 + H_2O + CO_2 \\to NH_4HCO_3$; $NH_4HCO_3 + NaCl \\to NaHCO_3 + NH_4Cl$ (NaHCO₃ less soluble, precipitates); $2NaHCO_3 \\xrightarrow{\\Delta} Na_2CO_3 + H_2O + CO_2$.',
        hindi: '**Na₂CO₃ (धोने का सोडा)** — **सोल्वे (अमोनिया-सोडा) प्रक्रिया**: $NH_3 + H_2O + CO_2 \\to NH_4HCO_3$; $NH_4HCO_3 + NaCl \\to NaHCO_3 + NH_4Cl$ (NaHCO₃ कम घुलनशील, अवक्षेपित); $2NaHCO_3 \\xrightarrow{\\Delta} Na_2CO_3 + H_2O + CO_2$.',
            },
            {
              english: '**NaHCO₃ (baking soda)** — antacid, fire extinguishers (acid-soda type), baking powder.',
        hindi: '**NaHCO₃ (बेकिंग सोडा)** - एंटासिड, अग्निशामक यंत्र (एसिड-सोडा प्रकार), बेकिंग पाउडर।',
            },
            {
              english: '**NaCl** — table salt; raw material for almost all sodium chemicals (Na, NaOH, Na₂CO₃, HCl).',
        hindi: '**NaCl** — टेबल नमक; लगभग सभी सोडियम रसायनों (Na, NaOH, Na₂CO₃, HCl) के लिए कच्चा माल।',
            },
            {
              english: 'Biological importance: Na⁺ regulates blood plasma volume & nerve impulses; K⁺ predominantly intracellular, activates enzymes.',
        hindi: 'जैविक महत्व: Na⁺ रक्त प्लाज्मा की मात्रा और तंत्रिका आवेगों को नियंत्रित करता है; K⁺ मुख्य रूप से अंतःकोशिकीय, एंजाइमों को सक्रिय करता है।',
            },
          ],
        },
        {
          type: 'note',
          text: {
            english: 'Solvay process **cannot** be used to make K₂CO₃ because KHCO₃ is too soluble to precipitate.',
        hindi: 'K₂CO₃ बनाने के लिए सॉल्वे प्रक्रिया **नहीं** का उपयोग किया जा सकता है क्योंकि KHCO₃ अवक्षेपण के लिए अत्यधिक घुलनशील है।',
          },
        },
      ],
    },
    {
      id: 'group2-general',
      title: {
        english: 'Group 2 — Alkaline-Earth Metals (Be, Mg, Ca, Sr, Ba, Ra)',
        hindi: 'समूह 2 - क्षारीय-पृथ्वी धातुएँ (Be, Mg, Ca, Sr, Ba, Ra)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Outer config $ns^2$; oxidation state +2. Smaller and denser than Gp 1 of same period; higher m.p./b.p. and IE.',
        hindi: 'बाहरी कॉन्फ़िगरेशन $ns^2$; ऑक्सीकरण अवस्था +2. समान अवधि के जीपी 1 से छोटा और सघन; उच्चतर एम.पी./बी.पी. और IE.',
            },
            {
              english: 'Down the group: radius and reducing nature increase; IE and electronegativity decrease.',
        hindi: 'समूह में नीचे: त्रिज्या और घटती प्रकृति में वृद्धि; IE और इलेक्ट्रोनगेटिविटी कम हो जाती है।',
            },
            {
              english: 'Flame colours: Be / Mg — none (electrons too tightly held); Ca brick-red, Sr crimson, Ba apple-green.',
        hindi: 'लौ के रंग: Be / Mg - कोई नहीं (इलेक्ट्रॉन बहुत कसकर पकड़े हुए); सीए ईंट-लाल, सीनियर क्रिमसन, बा सेब-हरा।',
            },
            {
              english: 'With **water**: Be does not react; Mg reacts only with hot water/steam; Ca, Sr, Ba react with cold water → $M(OH)_2 + H_2$.',
        hindi: '**पानी** के साथ: Be प्रतिक्रिया नहीं करता है; Mg केवल गर्म water/steam के साथ प्रतिक्रिया करता है; Ca, Sr, Ba ठंडे पानी के साथ प्रतिक्रिया करते हैं → $M(OH)_2 + H_2$।',
            },
            {
              english: 'With **O₂**: form MO; in excess O₂, Sr, Ba give peroxides too.',
        hindi: '**O₂** के साथ: फॉर्म MO; अधिक मात्रा में O₂, Sr, Ba पेरोक्साइड भी देते हैं।',
            },
            {
              english: 'With **N₂**: $3M + N_2 \\to M_3N_2$ (nitrides; only Gp 2 forms readily).',
        hindi: '**N₂** के साथ: $3M + N_2 \\to M_3N_2$ (नाइट्राइड्स; केवल Gp 2 आसानी से बनता है)।',
            },
            {
              english: 'With **acids**: liberate H₂.',
        hindi: '**अम्ल** के साथ: H₂ को मुक्त करें।',
            },
            {
              english: '**Solubility trends** (Gp 2): hydroxides — solubility **increases** down the group (Be(OH)₂ insoluble, Ba(OH)₂ very soluble); sulphates — solubility **decreases** (BeSO₄ soluble, BaSO₄ insoluble) due to fall in lattice energy being slower than fall in hydration energy.',
        hindi: '**घुलनशीलता प्रवृत्तियाँ** (जीपी 2): हाइड्रॉक्साइड्स - घुलनशीलता समूह में **बढ़ती** है (Be(OH)₂ अघुलनशील, Ba(OH)₂ बहुत घुलनशील); सल्फेट्स - जलयोजन ऊर्जा में गिरावट की तुलना में Common Trapी ऊर्जा में गिरावट के कारण घुलनशीलता **घटती है** (BeSO₄ घुलनशील, BaSO₄ अघुलनशील)।',
            },
            {
              english: 'Thermal stability of carbonates and nitrates **increases** down the group (larger cation stabilises larger anion).',
        hindi: 'समूह में नीचे की ओर कार्बोनेट और नाइट्रेट की थर्मल स्थिरता **बढ़ती** है (बड़ा धनायन बड़े आयन को स्थिर करता है)।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Anomalies of **Be** (small size, high charge density, no $d$-orbital, max covalency 4): does not react with water; forms covalent compounds (BeCl₂ polymeric in solid, dimer in vapour); amphoteric oxide / hydroxide; resembles Al (diagonal relationship).',
        hindi: '**Be** की विसंगतियाँ (छोटा आकार, उच्च चार्ज घनत्व, कोई $d$-कक्षीय, अधिकतम सहसंयोजकता 4): पानी के साथ प्रतिक्रिया नहीं करता है; सहसंयोजक यौगिक बनाता है (ठोस में BeCl₂ बहुलक, वाष्प में डिमर); एम्फोटेरिक ऑक्साइड / हाइड्रॉक्साइड; अल (विकर्ण संबंध) जैसा दिखता है।',
          },
        },
      ],
    },
    {
      id: 'group2-compounds',
      title: {
        english: 'Important Compounds of Calcium',
        hindi: 'कैल्शियम के महत्वपूर्ण यौगिक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**CaO (quicklime)** — $CaCO_3 \\xrightarrow{1070\\,K} CaO + CO_2$. With water: $CaO + H_2O \\to Ca(OH)_2$ (slaking). Basic flux in metallurgy.',
        hindi: '**CaO (क्विकटाइम)** — $CaCO_3 \\xrightarrow{1070\\,K} CaO + CO_2$। पानी के साथ: $CaO + H_2O \\to Ca(OH)_2$ (स्लेकिंग)। धातुकर्म में मूल प्रवाह.',
            },
            {
              english: '**Ca(OH)₂ (slaked lime)** — limewater; turns milky with CO₂ ($\\to CaCO_3$); used in mortar, bleaching powder, sewage treatment.',
        hindi: '**Ca(OH)₂ (बुझा हुआ चूना)** — चूना पानी; CO₂ ($\\to CaCO_3$) के साथ दूधिया हो जाता है; मोर्टार, ब्लीचिंग पाउडर, सीवेज उपचार में उपयोग किया जाता है।',
            },
            {
              english: '**CaCO₃ (limestone, marble)** — building material; manufacture of cement, quicklime, glass.',
        hindi: '**CaCO₃ (चूना पत्थर, संगमरमर)** — निर्माण सामग्री; सीमेंट, बुझा हुआ चूना, कांच का निर्माण।',
            },
            {
              english: '**CaSO₄·½H₂O (Plaster of Paris)** — $2CaSO_4\\cdot 2H_2O \\xrightarrow{393\\,K} 2CaSO_4\\cdot \\tfrac12 H_2O + 3H_2O$. On adding water it sets back to gypsum with slight expansion → orthopaedic casts, moulds.',
        hindi: '**CaSO₄·½H₂O (प्लास्टर ऑफ पेरिस)** — $2CaSO_4\\cdot 2H_2O \\xrightarrow{393\\,K} 2CaSO_4\\cdot \\tfrac12 H_2O + 3H_2O$. पानी डालने पर यह थोड़े विस्तार के साथ वापस जिप्सम में बदल जाता है → आर्थोपेडिक कास्ट, सांचे।',
            },
            {
              english: '**Cement** — Portland cement: clinker (CaO·SiO₂·Al₂O₃) + 2–5 % gypsum (to slow setting). Setting = hydration of calcium silicates and aluminates.',
        hindi: '**सीमेंट** - पोर्टलैंड सीमेंट: क्लिंकर (CaO·SiO₂·Al₂O₃) + 2-5% जिप्सम (धीमी सेटिंग के लिए)। सेटिंग = कैल्शियम सिलिकेट्स और एलुमिनेट्स का जलयोजन।',
            },
            {
              english: 'Biological role of Ca²⁺: bones (hydroxyapatite), teeth, blood-clotting, muscle contraction. Mg²⁺: enzymes (kinases), chlorophyll core.',
        hindi: 'Ca²⁺ की जैविक भूमिका: हड्डियाँ (हाइड्रॉक्सीएपेटाइट), दांत, रक्त का थक्का जमना, मांसपेशियों में संकुचन। Mg²⁺: एंजाइम (किनेसेस), क्लोरोफिल कोर।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '4Li + O_2 \\to 2Li_2O',
      desc: {
        english: 'Li gives only mono-oxide',
        hindi: 'ली केवल मोनो-ऑक्साइड देता है',
      },
    },
    {
      latex: '2Na + O_2 \\to Na_2O_2',
      desc: {
        english: 'Na gives peroxide',
        hindi: 'Na पेरोक्साइड देता है',
      },
    },
    {
      latex: 'K + O_2 \\to KO_2',
      desc: {
        english: 'K, Rb, Cs give super-oxide',
        hindi: 'K, Rb, Cs सुपर-ऑक्साइड देते हैं',
      },
    },
    {
      latex: '2NaHCO_3 \\xrightarrow{\\Delta} Na_2CO_3 + H_2O + CO_2',
      desc: {
        english: 'Solvay last step',
        hindi: 'सॉल्वे अंतिम चरण',
      },
    },
    {
      latex: 'CaO + H_2O \\to Ca(OH)_2',
      desc: {
        english: 'Slaking of lime',
        hindi: 'चूने का बुझना',
      },
    },
    {
      latex: '2CaSO_4{\\cdot}2H_2O \\xrightarrow{393\\,K} 2CaSO_4{\\cdot}\\tfrac12 H_2O',
      desc: {
        english: 'Plaster of Paris formation',
        hindi: 'प्लास्टर ऑफ पेरिस का निर्माण',
      },
    },
    {
      latex: '\\text{Reducing power (aq):}\\ Li > Cs > Na > K > Rb',
      desc: {
        english: 'Anomaly due to hydration of $Li^+$',
        hindi: '$Li^+$ के जलयोजन के कारण विसंगति',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Solvay',
      problem: {
        english: 'Solvay process cannot be used for the manufacture of:',
        hindi: 'सॉल्वे प्रक्रिया का उपयोग इसके निर्माण के लिए नहीं किया जा सकता है:',
      },
      answer: {
        english: 'K₂CO₃',
        hindi: 'K₂CO₃',
      },
      solution: {
        english: 'KHCO₃ is too soluble to precipitate.',
        hindi: 'KHCO₃ अवक्षेपण के लिए अत्यधिक घुलनशील है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Flame',
      problem: {
        english: 'Apple-green flame colour is given by:',
        hindi: 'सेब-हरा लौ रंग किसके द्वारा दिया जाता है:',
      },
      answer: {
        english: 'Ba',
        hindi: 'बा',
      },
      solution: {
        english: 'Ca brick-red, Sr crimson, Ba apple-green.',
        hindi: 'सीए ईंट-लाल, सीनियर क्रिमसन, बा सेब-हरा।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Solubility',
      problem: {
        english: 'Among Gp 2 sulphates, the most soluble in water is:',
        hindi: 'जीपी 2 सल्फेट्स में, पानी में सबसे घुलनशील है:',
      },
      answer: {
        english: 'BeSO₄',
        hindi: 'BeSO₄',
      },
      solution: {
        english: 'Hydration energy dominates for small Be²⁺.',
        hindi: 'छोटे Be²⁺ के लिए हाइड्रेशन ऊर्जा हावी है।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'PoP',
      problem: {
        english: 'Plaster of Paris is:',
        hindi: 'प्लास्टर ऑफ पेरिस है: (यानि )',
      },
      answer: {
        english: '$2CaSO_4\\cdot H_2O$ (i.e. $CaSO_4\\cdot\\tfrac12 H_2O$)',
        hindi: '$2CaSO_4\\cdot H_2O$ (यानि $CaSO_4\\cdot\\tfrac12 H_2O$)',
      },
      solution: {
        english: 'Hemihydrate of calcium sulphate.',
        hindi: 'कैल्शियम सल्फेट का हेमीहाइड्रेट।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Diagonal',
      problem: {
        english: 'Element that shows diagonal relationship with Al:',
        hindi: 'वह तत्व जो अल के साथ विकर्ण संबंध दर्शाता है:',
      },
      answer: {
        english: 'Be',
        hindi: 'होना',
      },
      solution: {
        english: 'Be ↔ Al — similar charge/size ratio.',
        hindi: 'Be ↔ Al - समान चार्ज/आकार अनुपात।',
      },
    },
  ],
  next: {
    slug: 'p-block-13',
    title: 'p-Block Group 13 — Boron Family',
  },
};

export default chapter;
